import { inject, Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { ReviewsPaginationDefaults } from '@app/salon-details/salon-reviews/pagination-defaults';
import { ISalonPage, SalonModulesEnum, ISalonReviews } from '@app/shared/models';
import { SalonService } from '@app/shared/services/salon/salon.service';
import { TreatmentService } from '@app/shared/services/treatment/treatment.service';
import { Observable, EMPTY, combineLatest, of, switchMap, catchError, map, shareReplay } from 'rxjs';
import { ISalonDetailsApi } from '@app/salon-details/models/salon-details-api.model';

@Injectable({
  providedIn: 'root'
})
export class SalonPageResolverService implements Resolve<ISalonPage> {
  private router = inject(Router);
  private salonService = inject(SalonService);
  private treatmentService = inject(TreatmentService);

  resolve(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<ISalonPage> {
    const salonId = route.paramMap.get('simpleFlowSalonId');

    if (salonId) {
      const salonDetails$ = this.salonService.getSalonDetails$(salonId);
      const salonTreatmentCategories$ = this.treatmentService.getTreatmentsByCategories(salonId);
      const salonReviews$ = this.getSalonReviews$(salonDetails$);

      return combineLatest([salonDetails$, salonTreatmentCategories$, salonReviews$])
        .pipe(
          map(([salonDetails, treatmentCategories, salonReviews]) => ({ salonDetails, treatmentCategories, salonReviews })),
          catchError(() => {
            this.router.navigate(['/']);
            return EMPTY;
          })
        );
    } else {
      const salonDetails$ = this.salonService.getSalonDetailsByCityAndAlias$(route.paramMap.get('cityName'), route.paramMap.get('salonNameAlias'))
        .pipe(shareReplay(1));
      const salonReviews$ = this.getSalonReviews$(salonDetails$);

      return salonDetails$
        .pipe(
          map((salonDetails: ISalonDetailsApi) => {
            const salonTreatmentCategories$ = this.treatmentService.getTreatmentsByCategories(salonDetails.id);

            return { salonDetails, salonTreatmentCategories$ };
          }),
          switchMap(({ salonDetails, salonTreatmentCategories$ }) => combineLatest([salonReviews$, salonTreatmentCategories$])
            .pipe(
              map(([salonReviews, treatmentCategories]) => ({ salonDetails, salonReviews, treatmentCategories }))
            )),
          catchError(() => {
            this.router.navigate(['/']);
            return EMPTY;
          })
        );
    }
  }

  private getSalonReviews$(salonDetails$: Observable<ISalonDetailsApi>): Observable<ISalonReviews> {
    return salonDetails$.pipe(
      switchMap((salonDetails: ISalonDetailsApi) => {
        if (salonDetails.modules?.includes(SalonModulesEnum.Reviews)
          && salonDetails.reviewsCount > 0) {
          return this.salonService.getSalonReviews$(
            salonDetails.id,
            ReviewsPaginationDefaults.reviewsPageSize,
            ReviewsPaginationDefaults.reviewsStartingPageNumber
          );
        }
        return of(null);
      }));
  }
}
